import { ChevronDown, ChevronRight } from 'lucide-react';
import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import { Button } from '../ui/Button.tsx';
import { cx } from '../ui/cx.ts';

/**
 * Collapsible disclosure card — local Dispatch Deck rebuild of the
 * console-shared TechnicalDetails factory (epic #897 PR 9). A plain
 * button + chevron + hidden region: no new deps, interactive chrome
 * from `ui/Button`, aria-expanded/aria-controls wiring for the region.
 *
 * Labels arrive translated from the caller (house rule: components in
 * `src/components` ship no copy of their own).
 */
interface Props {
  readonly id?: string;
  readonly title: string;
  readonly summary?: string;
  readonly showLabel: string;
  readonly hideLabel: string;
  readonly children: ReactNode;
  readonly className?: string;
  readonly testId: string;
}

export function Disclosure({
  id,
  title,
  summary,
  showLabel,
  hideLabel,
  children,
  className,
  testId,
}: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const regionId = useId();
  return (
    <section
      id={id}
      data-testid={testId}
      className={cx('rounded-lg border border-line-soft bg-card', className)}
    >
      <div className="flex items-start gap-3 px-4 py-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h2 className="m-0 text-section text-paper">{title}</h2>
          {summary !== undefined ? (
            <p className="m-0 text-dense text-paper-low">{summary}</p>
          ) : null}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setOpen((current) => !current)}
          aria-expanded={open}
          aria-controls={regionId}
          data-testid={`${testId}-toggle`}
        >
          {open ? (
            <ChevronDown aria-hidden="true" className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight aria-hidden="true" className="h-3.5 w-3.5" />
          )}
          {open ? hideLabel : showLabel}
        </Button>
      </div>
      {open ? (
        <div
          id={regionId}
          data-testid={`${testId}-content`}
          className="border-t border-line-soft px-4 py-4"
        >
          {children}
        </div>
      ) : null}
    </section>
  );
}
